import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { CopyField } from "@/components/ui/copy-field"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { formatCents } from "@/lib/fetch-json"

export interface DealRow {
  id: number
  name: string
  slug: string
  price_cents: number
  max_quantity: number | null
  redemptions_count: number
  expires_at: string | null
  active: boolean
  available: boolean
  expired: boolean
  sold_out: boolean
  checkout_url: string
}

export default function AdminLifetimeDealsIndex({ deals }: { deals: DealRow[] }) {
  return (
    <>
      <Head title="Lifetime Deals">
        <meta name="description" content="Create and manage Flywrite lifetime deals and their redemptions." />
        <meta property="og:title" content="Lifetime Deals" />
        <meta property="og:description" content="Create and manage Flywrite lifetime deals and their redemptions." />
      </Head>
      <AdminShell>
        <PageHeader
          title="Lifetime Deals"
          description={`${deals.length} ${deals.length === 1 ? "deal" : "deals"}. Each deal has a shareable checkout URL.`}
          actions={
            <Button size="sm" asChild>
              <Link href="/admin/lifetime_deals/new">New deal</Link>
            </Button>
          }
        />

        {deals.length === 0 ? (
          <p className="mt-8 text-sm text-ink-muted">
            No deals yet. <Link href="/admin/lifetime_deals/new">Create one</Link> to get started.
          </p>
        ) : (
          <div className="mt-6 space-y-4">
            {deals.map((deal) => (
              <DealCard key={deal.id} deal={deal} />
            ))}
          </div>
        )}
      </AdminShell>
    </>
  )
}

function DealCard({ deal }: { deal: DealRow }) {
  return (
    <div className="rounded-lg border border-hairline p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3>{deal.name}</h3>
            <AvailabilityBadge deal={deal} />
          </div>
          <p className="mt-1 text-sm text-ink-muted">
            {deal.price_cents === 0 ? "Free" : formatCents(deal.price_cents)} ·{" "}
            {deal.redemptions_count}
            {deal.max_quantity != null ? ` / ${deal.max_quantity}` : ""} redeemed
            {deal.expires_at ? ` · expires ${formatDate(deal.expires_at)}` : " · no expiry"}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="ghost" size="sm" asChild>
            <Link href={`/admin/lifetime_deals/${deal.id}/redemptions`}>Redemptions</Link>
          </Button>
          <Button variant="ghost" size="sm" asChild>
            <Link href={`/admin/lifetime_deals/${deal.id}/edit`}>Edit</Link>
          </Button>
          <DeleteDealDialog deal={deal} />
        </div>
      </div>
      <div className="mt-3 max-w-xl">
        <CopyField value={deal.checkout_url} ariaLabel={`Copy checkout URL for ${deal.name}`} />
      </div>
    </div>
  )
}

function AvailabilityBadge({ deal }: { deal: DealRow }) {
  if (!deal.active) return <Badge tone="muted">Inactive</Badge>
  if (deal.expired) return <Badge tone="solid">Expired</Badge>
  if (deal.sold_out) return <Badge tone="signal">Sold out</Badge>
  return <Badge tone="accent">Available</Badge>
}

function DeleteDealDialog({ deal }: { deal: DealRow }) {
  const [open, setOpen] = useState(false)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          Delete
        </Button>
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Delete “{deal.name}”?</DialogTitle>
          <DialogDescription>
            {deal.redemptions_count > 0
              ? "This deal has redemptions, so it can't be deleted — deactivate it instead by unchecking Active."
              : "The deal and its checkout URL will stop working. This can't be undone."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          {deal.redemptions_count === 0 && (
            <Button
              variant="danger"
              onClick={() =>
                router.delete(`/admin/lifetime_deals/${deal.id}`, {
                  preserveScroll: true,
                  onSuccess: () => setOpen(false),
                })
              }
            >
              Delete deal
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
